window.MultishipAddressSync = window.MultishipAddressSync || class MultishipAddressSync {
  constructor(storage, scheduleTask) {
    this.storage = storage === undefined ? this.getDefaultStorage() : storage;
    this.scheduleTask = scheduleTask || ((callback) => setTimeout(callback, 0));
    this.pendingStorageKey = "lcf:multiship:pending-addresses:v1";
    this.pendingFingerprints = this.loadPendingFingerprints();
    this.forms = new Set();
    this.reconciliationScheduled = false;
    this.reconciliationRequested = false;
    this.reconciliationPromise = null;
  }

  getDefaultStorage() {
    try {
      return window.localStorage;
    } catch (error) {
      console.warn("Local storage is unavailable for multiship address sync", error);
      return null;
    }
  }

  markAddressPending(address) {
    this.refreshPendingFingerprints();
    this.pendingFingerprints.add(this.getAddressFingerprint(address));
    this.savePendingFingerprints();
  }

  clearPendingAddress(address) {
    this.refreshPendingFingerprints();
    this.pendingFingerprints.delete(this.getAddressFingerprint(address));
    this.savePendingFingerprints();
  }

  hasPendingAddress(address) {
    return this.pendingFingerprints.has(this.getAddressFingerprint(address));
  }

  register(form) {
    if (!this.shouldReconcile(form)) return;

    this.forms.add(form);

    if (this.reconciliationPromise) {
      this.reconciliationRequested = true;
      return;
    }

    this.scheduleReconciliation();
  }

  shouldReconcile(form) {
    const isCartPage = form.getAttribute("data-reconcile-pending-addresses") === "true";

    return isCartPage &&
      Boolean(form.customerId) &&
      form.isAddressComplete() &&
      this.hasPendingAddress(form.addressData);
  }

  scheduleReconciliation() {
    if (this.reconciliationScheduled || this.reconciliationPromise) return;

    this.reconciliationScheduled = true;
    this.scheduleTask(() => {
      this.reconciliationScheduled = false;
      this.reconcile().catch((error) => {
        console.error("Error reconciling multiship customer addresses", error);
      });
    });
  }

  async reconcile() {
    if (this.reconciliationPromise) return this.reconciliationPromise;

    this.reconciliationRequested = false;
    this.reconciliationPromise = this.reconcilePendingAddresses();

    try {
      return await this.reconciliationPromise;
    } finally {
      this.reconciliationPromise = null;

      if (this.reconciliationRequested) {
        this.scheduleReconciliation();
      }
    }
  }

  async reconcilePendingAddresses() {
    const forms = this.getEligibleForms();
    const addresses = this.getUniquePendingAddresses(forms);

    if (addresses.length === 0) return [];

    const addressBookForm = forms[0];
    const addressBookLoaded = await addressBookForm.fetchAllAddresses();
    if (!addressBookLoaded) return [];

    const results = [];
    for (const address of addresses) {
      const result = await addressBookForm.saveNewAddressToAccount(address);
      results.push(result);

      if (result.success || result.reason === "duplicate") {
        this.clearPendingAddress(address);
      }
    }

    return results;
  }

  getEligibleForms() {
    const eligibleForms = [];

    this.forms.forEach((form) => {
      if (form.isConnected === false) {
        this.forms.delete(form);
      } else if (this.shouldReconcile(form)) {
        eligibleForms.push(form);
      }
    });

    return eligibleForms;
  }

  getUniquePendingAddresses(forms) {
    const addressesByFingerprint = new Map();

    forms.forEach((form) => {
      const fingerprint = this.getAddressFingerprint(form.addressData);
      addressesByFingerprint.set(fingerprint, form.addressData);
    });

    return Array.from(addressesByFingerprint.values());
  }

  addressesMatch(firstAddress, secondAddress) {
    return this.getAddressKey(firstAddress) === this.getAddressKey(secondAddress);
  }

  getAddressFingerprint(address) {
    const addressKey = this.getAddressKey(address);
    const firstHash = this.hashAddressKey(addressKey, 2166136261);
    const secondHash = this.hashAddressKey(addressKey, 3339675911);
    return `${firstHash}-${secondHash}`;
  }

  getAddressKey(address) {
    return [
      address.address1,
      address.city,
      address.province_code || address.province,
      address.zip,
      address.country_code || address.country,
    ].map((value) => this.normalizeAddressValue(value)).join("|");
  }

  normalizeAddressValue(value) {
    return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  hashAddressKey(addressKey, seed) {
    let hash = seed;

    for (let index = 0; index < addressKey.length; index += 1) {
      hash ^= addressKey.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
  }

  refreshPendingFingerprints() {
    this.pendingFingerprints = this.loadPendingFingerprints();
  }

  loadPendingFingerprints() {
    if (!this.storage) return new Set();

    try {
      const fingerprints = JSON.parse(this.storage.getItem(this.pendingStorageKey) || "[]");
      return new Set(Array.isArray(fingerprints) ? fingerprints : []);
    } catch (error) {
      console.warn("Unable to load pending multiship addresses", error);
      return new Set();
    }
  }

  savePendingFingerprints() {
    if (!this.storage) return;

    try {
      const fingerprints = Array.from(this.pendingFingerprints);

      if (fingerprints.length === 0) {
        this.storage.removeItem(this.pendingStorageKey);
      } else {
        this.storage.setItem(this.pendingStorageKey, JSON.stringify(fingerprints));
      }
    } catch (error) {
      console.warn("Unable to save pending multiship addresses", error);
    }
  }
};

window.multishipAddressSync = window.multishipAddressSync || new window.MultishipAddressSync();
